/**
 * Portfolio apps — icons + content components.
 *
 * These are intentionally lightweight placeholders wired through the shared
 * `appKit` primitives so the full shell (dock, windows, spotlight, mobile
 * launcher) renders end-to-end today. Real, hand-designed content for each app
 * — and real Calculator / Clock ports — land in a follow-up pass; static copy
 * will live in `config/portfolio.ts`.
 *
 * Note: the Browser is a `type:"browser"` iframe window (see desktop-config),
 * so it needs an icon here but no content component.
 */
import { makeIcon, PlaceholderBody, PortfolioContent } from "./appKit";

/* --------------------------------------------------------------------------
   Icons (dock + titlebar) — one gradient/glyph per app
   -------------------------------------------------------------------------- */

export const { Icon: AboutIcon, TitleIcon: AboutTitleIcon } = makeIcon(
  "👋",
  "#6366f1",
  "#4338ca"
);
export const { Icon: ResumeIcon, TitleIcon: ResumeTitleIcon } = makeIcon(
  "📄",
  "#0ea5e9",
  "#0369a1"
);
export const { Icon: ExperienceIcon, TitleIcon: ExperienceTitleIcon } =
  makeIcon("💼", "#f59e0b", "#b45309");
export const { Icon: SkillsIcon, TitleIcon: SkillsTitleIcon } = makeIcon(
  "🧰",
  "#10b981",
  "#047857"
);
export const { Icon: ProjectsIcon, TitleIcon: ProjectsTitleIcon } = makeIcon(
  "🚀",
  "#ec4899",
  "#be185d"
);
export const { Icon: ContactIcon, TitleIcon: ContactTitleIcon } = makeIcon(
  "✉️",
  "#8b5cf6",
  "#6d28d9"
);
export const { Icon: SettingsIcon, TitleIcon: SettingsTitleIcon } = makeIcon(
  "⚙️",
  "#64748b",
  "#334155"
);
export const { Icon: CalculatorIcon, TitleIcon: CalculatorTitleIcon } =
  makeIcon("🧮", "#f97316", "#c2410c");
export const { Icon: ClockIcon, TitleIcon: ClockTitleIcon } = makeIcon(
  "⏱️",
  "#ef4444",
  "#b91c1c"
);
export const { Icon: BrowserIcon, TitleIcon: BrowserTitleIcon } = makeIcon(
  "🌐",
  "#3b82f6",
  "#1d4ed8"
);

/* --------------------------------------------------------------------------
   Content components
   -------------------------------------------------------------------------- */

export function AboutDemo() {
  return (
    <PortfolioContent
      title="Michael Weber"
      subtitle="Software Engineer · Builder of desktop-web.com"
    >
      <p style={{ fontSize: 15, lineHeight: 1.7, marginTop: 0 }}>
        Welcome to my desktop. This site is a macOS/iOS-style shell that doubles
        as my portfolio — poke around the dock to explore my experience, skills,
        projects, and how to get in touch.
      </p>
      <PlaceholderBody note="Placeholder bio. Real About content (photo, one-liner, links) is filled in from config/portfolio.ts in the next pass." />
    </PortfolioContent>
  );
}

export function ResumeDemo() {
  return (
    <PortfolioContent
      title="Résumé"
      subtitle="Download or read inline"
      maxWidth={900}
    >
      <div style={{ marginBottom: 16 }}>
        <a
          href="/resume.pdf"
          download
          style={{
            display: "inline-block",
            background: "#4338ca",
            color: "#fff",
            padding: "10px 18px",
            borderRadius: 10,
            fontWeight: 600,
            textDecoration: "none",
          }}
        >
          ↓ Download PDF
        </a>
      </div>
      <PlaceholderBody note="Add a real public/resume.pdf and this will embed it inline below the download button." />
    </PortfolioContent>
  );
}

export function ExperienceDemo() {
  return (
    <PortfolioContent title="Experience" subtitle="Work history">
      <PlaceholderBody note="Placeholder. A work-history timeline / cards driven by a static data array lands here." />
    </PortfolioContent>
  );
}

export function SkillsDemo() {
  return (
    <PortfolioContent title="Skills" subtitle="Tools & technologies">
      <PlaceholderBody note="Placeholder. A skills grid grouped by category (static data) lands here." />
    </PortfolioContent>
  );
}

export function ProjectsDemo() {
  return (
    <PortfolioContent title="Projects" subtitle="Selected work">
      <PlaceholderBody note="Placeholder. Project showcase cards with links — including this desktop as the flagship — land here." />
    </PortfolioContent>
  );
}

export function ContactDemo() {
  return (
    <PortfolioContent title="Contact" subtitle="Let's talk">
      <PlaceholderBody note="Placeholder. Email (mailto), GitHub, LinkedIn, and X links land here." />
    </PortfolioContent>
  );
}

export function SettingsDemo() {
  return (
    <PortfolioContent title="Settings" subtitle="Appearance">
      <PlaceholderBody note="Placeholder. The stripped-down local Settings (theme toggle + wallpaper picker) lands here." />
    </PortfolioContent>
  );
}

export function CalculatorDemo() {
  return (
    <PortfolioContent title="Calculator">
      <PlaceholderBody note="Placeholder. The self-contained Calculator is ported from the source app in the next pass." />
    </PortfolioContent>
  );
}

export function ClockDemo() {
  return (
    <PortfolioContent title="Clock">
      <PlaceholderBody note="Placeholder. The self-contained Clock/Stopwatch is ported from the source app in the next pass." />
    </PortfolioContent>
  );
}
